import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isValidIanaTimeZone,
  POLLER_HHMM,
  POLLER_MAX_FREQUENCY_MINUTES,
  POLLER_MIN_FREQUENCY_MINUTES,
  type PollerConfig,
  type PollerConfigUpdate,
} from '@app/shared';
import {
  Banner,
  Button,
  Cluster,
  FormField,
  Heading,
  SelectMenu,
  Stack,
  Surface,
  Switch,
  Text,
  TextInput,
  type SelectMenuOption,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { QueryState } from './QueryState';

const POLLER_CONFIG_QUERY_KEY = ['admin', 'poller-config'] as const;

/** A short, curated set of common IANA zones for the timezone picker. The current value
 * and the viewer's detected zone are always folded in so any saved zone stays selectable. */
const COMMON_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/** The viewer's detected IANA zone, or `undefined` if the host can't resolve one. */
function detectedTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Operator-only "Email poller" settings — edits the single GLOBAL poller config
 * (`GET`/`PUT /api/admin/poller-config`, operator-gated server-side).
 *
 * Gating is try-load-then-hide: the auth `me` payload carries no operator flag, so we
 * simply attempt the read. A non-operator's GET returns 403 (401 anonymous) — in that
 * case we render NOTHING (not even the heading), so normal admins never see a broken or
 * forbidden section. Operators get the editor.
 */
export function PollerConfigSection() {
  const query = useQuery({
    queryKey: POLLER_CONFIG_QUERY_KEY,
    queryFn: () => api.getPollerConfig(),
    retry: false,
  });

  // Operator gate: hide the whole section for anyone the server won't let read the config.
  const status = query.error instanceof ApiError ? query.error.status : undefined;
  if (status === 403 || status === 401) return null;
  // Until the gate resolves, render nothing so non-operators never flash the heading.
  if (query.isPending) return null;

  return (
    <Surface bordered radius="lg" padding="5" data-testid="poller-config-section">
      <Stack gap="4">
        <Stack gap="1">
          <Heading level={2} size="md">
            Email poller
          </Heading>
          <Text size="sm" tone="secondary">
            Platform-wide settings for the single email-poller loop. These apply to every
            workspace — only platform operators can see or change them.
          </Text>
        </Stack>

        <QueryState query={query} loading={null}>
          {(config) => <PollerConfigForm config={config} />}
        </QueryState>
      </Stack>
    </Surface>
  );
}

/** The editable form, seeded once from the loaded config. */
function PollerConfigForm({ config }: { config: PollerConfig }) {
  const queryClient = useQueryClient();

  const [frequency, setFrequency] = useState(String(config.checkFrequencyMinutes));
  const [activeHoursEnabled, setActiveHoursEnabled] = useState(config.activeHoursEnabled);
  const [activeStart, setActiveStart] = useState(config.activeHoursStart);
  const [activeEnd, setActiveEnd] = useState(config.activeHoursEnd);
  const [timezone, setTimezone] = useState(config.activeHoursTimezone);
  const [filterNonActionable, setFilterNonActionable] = useState(config.filterNonActionable);
  const [saved, setSaved] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const timezoneOptions = useMemo<SelectMenuOption[]>(() => {
    const values = Array.from(
      new Set([timezone, detectedTimeZone(), ...COMMON_TIMEZONES].filter((v): v is string =>
        isValidIanaTimeZone(v),
      )),
    );
    return values.map((value) => ({ value, label: value }));
  }, [timezone]);

  // Validation mirrors the Zod contract (packages/shared poller-config).
  const freqNum = Number(frequency);
  const freqError =
    frequency.trim() === '' ||
    !Number.isInteger(freqNum) ||
    freqNum < POLLER_MIN_FREQUENCY_MINUTES ||
    freqNum > POLLER_MAX_FREQUENCY_MINUTES
      ? `Enter a whole number of minutes between ${POLLER_MIN_FREQUENCY_MINUTES} and ${POLLER_MAX_FREQUENCY_MINUTES}.`
      : undefined;
  const startError = !POLLER_HHMM.test(activeStart) ? 'Use a 24-hour time, HH:MM.' : undefined;
  const endError = !POLLER_HHMM.test(activeEnd) ? 'Use a 24-hour time, HH:MM.' : undefined;
  const tzError = !isValidIanaTimeZone(timezone) ? 'Choose a valid time zone.' : undefined;

  const hasError = Boolean(
    freqError || (activeHoursEnabled && (startError || endError || tzError)),
  );

  const dirty =
    frequency !== String(config.checkFrequencyMinutes) ||
    activeHoursEnabled !== config.activeHoursEnabled ||
    activeStart !== config.activeHoursStart ||
    activeEnd !== config.activeHoursEnd ||
    timezone !== config.activeHoursTimezone ||
    filterNonActionable !== config.filterNonActionable;

  const save = useMutation({
    mutationFn: (body: PollerConfigUpdate) => api.updatePollerConfig(body),
    onSuccess: (updated) => {
      setSaved(true);
      setErrorText(null);
      // Refresh the cached config so `updatedAt` and the dirty baseline stay in sync.
      queryClient.setQueryData(POLLER_CONFIG_QUERY_KEY, updated);
      void queryClient.invalidateQueries({ queryKey: POLLER_CONFIG_QUERY_KEY });
    },
    onError: (err) => {
      setSaved(false);
      setErrorText(err instanceof ApiError ? err.message : 'Could not save settings.');
    },
  });

  const onSave = () => {
    if (hasError) return;
    save.mutate({
      checkFrequencyMinutes: freqNum,
      activeHoursEnabled,
      activeHoursStart: activeStart,
      activeHoursEnd: activeEnd,
      activeHoursTimezone: timezone,
      filterNonActionable,
    });
  };

  // Any edit clears a prior "saved" confirmation so it can't linger over new changes.
  const onEdit = <T,>(setter: (v: T) => void) => (value: T) => {
    setSaved(false);
    setter(value);
  };

  return (
    <Stack gap="5">
      {saved && (
        <Banner tone="success" onDismiss={() => setSaved(false)} data-testid="poller-saved-banner">
          Poller settings saved.
        </Banner>
      )}
      {errorText && (
        <Banner tone="danger" onDismiss={() => setErrorText(null)} data-testid="poller-error-banner">
          {errorText}
        </Banner>
      )}

      <div style={{ maxWidth: '14rem' }}>
        <FormField
          label="Check frequency (minutes)"
          help={`How often the poller checks for new mail. ${POLLER_MIN_FREQUENCY_MINUTES}–${POLLER_MAX_FREQUENCY_MINUTES} minutes (1440 = once a day).`}
          error={freqError}
        >
          <TextInput
            type="number"
            min={POLLER_MIN_FREQUENCY_MINUTES}
            max={POLLER_MAX_FREQUENCY_MINUTES}
            step={1}
            inputMode="numeric"
            value={frequency}
            onChange={(e) => onEdit(setFrequency)(e.target.value)}
            data-testid="poller-frequency"
          />
        </FormField>
      </div>

      <Stack gap="3">
        <Stack gap="1">
          <Switch
            checked={activeHoursEnabled}
            onChange={(e) => onEdit(setActiveHoursEnabled)(e.target.checked)}
            label="Only poll during active hours"
            data-testid="poller-active-hours-toggle"
          />
          <Text size="sm" tone="secondary">
            When on, the poller runs only inside the window below. Overnight windows are
            allowed — a start after the end (e.g. 22:00 → 06:00) wraps past midnight.
          </Text>
        </Stack>

        {activeHoursEnabled && (
          <Cluster gap="4" align="start" wrap>
            <div style={{ flex: '0 0 8rem' }}>
              <FormField label="Start" error={startError}>
                <TextInput
                  type="time"
                  value={activeStart}
                  onChange={(e) => onEdit(setActiveStart)(e.target.value)}
                  data-testid="poller-active-start"
                />
              </FormField>
            </div>
            <div style={{ flex: '0 0 8rem' }}>
              <FormField label="End" error={endError}>
                <TextInput
                  type="time"
                  value={activeEnd}
                  onChange={(e) => onEdit(setActiveEnd)(e.target.value)}
                  data-testid="poller-active-end"
                />
              </FormField>
            </div>
            <Stack gap="1" style={{ minWidth: '13rem' }}>
              <Text size="sm" weight="medium">
                Time zone
              </Text>
              <SelectMenu
                label="Active-hours time zone"
                value={timezone}
                onChange={onEdit(setTimezone)}
                options={timezoneOptions}
                minWidth="13rem"
                data-testid="poller-timezone"
              />
              {tzError && (
                <Text size="sm" tone="danger">
                  {tzError}
                </Text>
              )}
            </Stack>
          </Cluster>
        )}
      </Stack>

      <Stack gap="1">
        <Switch
          checked={filterNonActionable}
          onChange={(e) => onEdit(setFilterNonActionable)(e.target.checked)}
          label="Auto-skip non-actionable email"
          data-testid="poller-filter-toggle"
        />
        <Text size="sm" tone="secondary">
          Skip mail that clearly needs no reply — no-reply senders, bulk/list mail, delivery
          notifications.
        </Text>
      </Stack>

      <Cluster gap="3" align="center">
        <Button
          onClick={onSave}
          disabled={!dirty || hasError}
          loading={save.isPending}
          data-testid="poller-save"
        >
          Save
        </Button>
        {!dirty && !save.isPending && (
          <Text size="sm" tone="muted">
            No unsaved changes.
          </Text>
        )}
      </Cluster>
    </Stack>
  );
}
